import type { EditorDoc, Vec3 } from '@roomtreatment/core';
import {
  clampPose,
  inferSurface,
  poseToTransform,
  sameSurface,
  transformToPose,
  type SurfaceFrame,
} from './panelSurface';

/** How far off its surface a panel may sit and still count as mounted on it.
 *  Past this it's floating (see PanelPlacement.floating) and isn't ours to
 *  move — sliding a panel the user has deliberately pulled off the wall would
 *  be a surprise, not a fix. */
const SEATED_OFFSET_M = 0.05;

/** Below this a "move" is numerical noise from the pose round-trip, not a
 *  real slide, and dispatching it would dirty the simulation for nothing. */
const MOVED_EPSILON_M = 1e-4;

/** One panel's re-clamped world transform, ready for SYNC_TRANSFORM. */
export interface PanelMove {
  id: string;
  position: Vec3;
  rotation: Vec3;
}

/**
 * Panels on `frame` that would now sit over an opening, slid clear of it.
 *
 * Call after a window on that surface is added, resized or reshaped: a plan
 * showing a panel across a window isn't buildable, and silently leaving one
 * there would be a lie. Each result is a `SYNC_TRANSFORM` the caller
 * dispatches — same commit path a drag uses, so hand-placed and auto-cleared
 * panels can't drift apart.
 *
 * `doc` must already contain the new/updated windows; the avoidance itself
 * lives in `clampPose` (see `avoidWindows`), which reads them off `doc`. That
 * indirection is deliberate — anything else that ever blocks a surface teaches
 * `clampPose` about itself and every caller of this function inherits it.
 *
 * Corner traps are skipped: a corner has no u-extent to slide along, and a
 * window can't be stamped across a corner in the first place.
 */
export function panelsClearedOf(doc: EditorDoc, frame: SurfaceFrame): PanelMove[] {
  const moves: PanelMove[] = [];
  for (const p of doc.panels) {
    // Redundant with the `inferSurface` call below — it matches corner-shaped
    // panels only against corner frames — so nothing here is the sole thing
    // keeping a trap out of the result, and there is no test pinning this
    // line (removing it changes no observable behaviour, verified). Kept as a
    // cheap, intention-revealing early-out; don't write a test that claims it
    // is load-bearing.
    if (p.shape === 'corner') continue;
    if (!sameSurface(inferSurface(p, [frame])?.ref ?? null, frame.ref)) continue;
    const { pose, offset } = transformToPose(frame, p.position, p.rotation, p.size);
    if (Math.abs(offset) > SEATED_OFFSET_M) continue;
    const next = clampPose(frame, pose, p.size, doc);
    if (Math.hypot(next.u - pose.u, next.v - pose.v) < MOVED_EPSILON_M) continue;
    const t = poseToTransform(frame, next, p.size);
    moves.push({ id: p.id, position: t.position, rotation: t.rotation });
  }
  return moves;
}
